import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Stack,
  IconButton,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  FormHelperText,
  Divider,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { CreateLitterDTO, LitterPupDTO, Animal } from '../../types';
import littersService from '@services/littersService';

interface LitterDialogProps {
  open: boolean;
  onClose: () => void;
  motherId?: string;
  fatherId?: string;
  onSuccess: () => void;
}

const LitterDialog: React.FC<LitterDialogProps> = ({
  open,
  onClose,
  motherId,
  fatherId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data
  const [selectedMother, setSelectedMother] = useState<Animal | null>(null);
  const [selectedFather, setSelectedFather] = useState<Animal | null>(null);
  const [whelpingDate, setWhelpingDate] = useState('');
  const [pupsDeclared, setPupsDeclared] = useState(1);
  const [notes, setNotes] = useState('');
  const [pups, setPups] = useState<LitterPupDTO[]>([]);

  // Autocomplete states
  const [motherSearch, setMotherSearch] = useState('');
  const [fatherSearch, setFatherSearch] = useState('');
  const [motherOptions, setMotherOptions] = useState<Animal[]>([]);
  const [fatherOptions, setFatherOptions] = useState<Animal[]>([]);
  const [searchingMother, setSearchingMother] = useState(false);
  const [searchingFather, setSearchingFather] = useState(false);
  const [fatherFreeInput, setFatherFreeInput] = useState('');

  const debouncedMotherSearch = useDebounce(motherSearch, 400);
  const debouncedFatherSearch = useDebounce(fatherSearch, 400);

  // Preincarca parintele pe baza ID-ului primit (motherId sau fatherId)
  useEffect(() => {
    const loadParentAnimal = async () => {
      console.log('🔍 LitterDialog useEffect - open:', open, 'motherId:', motherId, 'fatherId:', fatherId);
      
      if (!open) {
        // Reseteaza starile cand dialogul se inchide
        console.log('🔍 LitterDialog - Resetting states because dialog closed');
        setSelectedMother(null);
        setSelectedFather(null);
        setMotherSearch('');
        setFatherSearch('');
        setWhelpingDate('');
        setPupsDeclared(1);
        setNotes('');
        setPups([]);
        setError(null);
        return;
      }

      try {
        if (motherId) {
          console.log('🔍 LitterDialog - Loading mother with ID:', motherId);
          // Incarca animalul mama
          const animal = await littersService.getAnimalById(motherId);
          console.log('🔍 LitterDialog - Mother animal loaded:', animal);
          if (animal) {
            setSelectedMother(animal);
            const searchText = animal.name + (animal.microchipId ? ` (${animal.microchipId})` : '');
            console.log('🔍 LitterDialog - Setting mother search text:', searchText);
            setMotherSearch(searchText);
          }
        }
        if (fatherId) {
          console.log('🔍 LitterDialog - Loading father with ID:', fatherId);
          // Incarca animalul tata
          const animal = await littersService.getAnimalById(fatherId);
          console.log('🔍 LitterDialog - Father animal loaded:', animal);
          if (animal) {
            setSelectedFather(animal);
            const searchText = animal.name + (animal.microchipId ? ` (${animal.microchipId})` : '');
            console.log('🔍 LitterDialog - Setting father search text:', searchText);
            setFatherSearch(searchText);
          }
        }
      } catch (err) {
        console.error('🔍 LitterDialog - Error loading parent animal:', err);
      }
    };

    loadParentAnimal();
  }, [motherId, fatherId, open]);

  // Eliminat preselect placeholder; folosim incarcarea reala din loadParentAnimal()

  // Initialize pups array when pupsDeclared changes
  useEffect(() => {
    if (pupsDeclared > pups.length) {
      // Add new slots
      const newPups = [...pups];
      for (let i = pups.length; i < pupsDeclared; i++) {
        newPups.push({
          mode: 'create',
          microchip: '',
          sex: 'U',
          name: `Pui ${i + 1}`,
        });
      }
      setPups(newPups);
    } else if (pupsDeclared < pups.length) {
      // Remove slots (with confirmation if they have data)
      const hasData = pups.slice(pupsDeclared).some(p => p.microchip || p.name);
      if (hasData) {
        if (window.confirm('Unele sloturi au date completate. Sigur doriti sa le stergeti?')) {
          setPups(pups.slice(0, pupsDeclared));
        } else {
          setPupsDeclared(pups.length);
        }
      } else {
        setPups(pups.slice(0, pupsDeclared));
      }
    }
  }, [pupsDeclared]);

  // Search for mother
  useEffect(() => {
    if (motherId) return; // nu cauta daca mama e preselectata
    if (debouncedMotherSearch && debouncedMotherSearch.length >= 2) {
      searchAnimals(debouncedMotherSearch, 'female', setMotherOptions, setSearchingMother);
    }
  }, [debouncedMotherSearch, motherId]);

  // Search for father
  useEffect(() => {
    if (fatherId) return; // nu cauta daca tatal e preselectat
    if (debouncedFatherSearch && debouncedFatherSearch.length >= 2) {
      searchAnimals(debouncedFatherSearch, 'male', setFatherOptions, setSearchingFather);
    }
  }, [debouncedFatherSearch, fatherId]);

  const searchAnimals = async (
    query: string,
    gender: 'male' | 'female',
    setOptions: (animals: Animal[]) => void,
    setSearching: (searching: boolean) => void
  ) => {
    try {
      setSearching(true);
      const results = await littersService.lookupAnimals(query);
      const filtered = results.filter((a: Animal) => a.gender === gender);
      setOptions(filtered);
    } catch (err) {
      console.error('Error searching animals:', err);
    } finally {
      setSearching(false);
    }
  };

  // Resolve father from free text (microchip sau nume)
  const resolveFatherFromFreeInput = async () => {
    const q = fatherFreeInput.trim();
    if (!q) {
      setSelectedFather(null);
      return;
    }
    try {
      const results = await littersService.lookupAnimals(q);
      const males = results.filter((a: any) => a.gender === 'male');
      if (males.length > 0) {
        setSelectedFather(males[0]);
      } else {
        setSelectedFather(null);
      }
    } catch (err) {
      console.error('Error resolving father from input:', err);
    }
  };

  const handlePupChange = (index: number, field: keyof LitterPupDTO, value: any) => {
    const newPups = [...pups];
    newPups[index] = { ...newPups[index], [field]: value };
    setPups(newPups);
  };

  const validateMicrochip = async (microchip: string, index: number) => {
    if (!microchip) return;

    try {
      const result = await littersService.checkMicrochipUnique(microchip);
      if (!result.unique) {
        // Microchip exists - offer to link
        if (window.confirm(`Microchip-ul ${microchip} exista deja in sistem. Doriti sa legati la animalul existent?`)) {
          handlePupChange(index, 'mode', 'link');
          handlePupChange(index, 'animalId', result.animalId);
          if (result.animal) {
            handlePupChange(index, 'name', result.animal.name);
            handlePupChange(index, 'sex', result.animal.gender === 'male' ? 'M' : result.animal.gender === 'female' ? 'F' : 'U');
          }
        }
      }
    } catch (err) {
      console.error('Error validating microchip:', err);
    }
  };

  const handleSave = async () => {
    // Validations
    if (!selectedMother || !selectedMother.id) {
      setError('Mama este obligatorie');
      return;
    }

    if (!whelpingDate) {
      setError('Data fatarii este obligatorie');
      return;
    }

    if (pups.some(p => !p.microchip)) {
      setError('Toate sloturile trebuie sa aiba microchip completat');
      return;
    }

    // Validate mode=create has required fields
    const invalidPups = pups.filter(
      p => p.mode === 'create' && (!p.species || !p.birthdate)
    );
    if (invalidPups.length > 0) {
      setError('Puii creati necesita specie si data nasterii');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data: CreateLitterDTO = {
        motherId: selectedMother.id,
        fatherId: selectedFather?.id || null,
        whelpingDate,
        pupsDeclared,
        notes,
        pups: pups.map(p => ({
          ...p,
          species: p.species || selectedMother.species,
          breed: p.breed || selectedMother.breed,
          birthdate: p.birthdate || whelpingDate,
        })),
      };

      const result = await littersService.createLitter(data);

      if (result.errors && result.errors.length > 0) {
        setError(`Fatare creata cu erori: ${result.errors.join(', ')}`);
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('Error creating litter:', err);
      const status = err?.response?.status;
      if (status === 403) {
        setError('Nu aveti permisiunea sa creati fatari cu acest cont. Incercati din contul clinicii.');
      } else if (status === 422) {
        setError(err.response?.data?.error || 'Date invalide pentru fatare');
      } else if (status === 409) {
        setError(err.response?.data?.error || 'Conflict de date (verificati parintii / microchipurile)');
      } else {
        setError('Eroare la crearea fatarii');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setSelectedMother(null);
      setSelectedFather(null);
      setWhelpingDate('');
      setPupsDeclared(1);
      setNotes('');
      setPups([]);
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Adauga Fatare</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Mama */}
          <Autocomplete
            value={selectedMother}
            onChange={(_, newValue) => setSelectedMother(newValue)}
            inputValue={motherSearch}
            onInputChange={(_, newInputValue) => setMotherSearch(newInputValue)}
            options={motherOptions}
            getOptionLabel={(option) => `${option.name} (${option.microchipId || 'fara CIP'})`}
            loading={searchingMother}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mama *"
                placeholder="Cauta dupa nume sau microchip"
                helperText="Selectati mama (sex feminin)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchingMother ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={loading || Boolean(motherId)}
          />

          {/* Tata (optional) */}
          {motherId ? (
            <TextField
              label="Tata (optional)"
              placeholder="Introduceti microchip sau nume (liber)"
              value={fatherFreeInput}
              onChange={(e) => setFatherFreeInput(e.target.value)}
              onBlur={resolveFatherFromFreeInput}
              helperText="Camp neobligatoriu. Vom incerca sa identificam automat tatal dupa microchip/nume."
              disabled={loading}
            />
          ) : (
            <Autocomplete
              value={selectedFather}
              onChange={(_, newValue) => setSelectedFather(newValue)}
              inputValue={fatherSearch}
              onInputChange={(_, newInputValue) => setFatherSearch(newInputValue)}
              options={fatherOptions}
              getOptionLabel={(option) => `${option.name} (${option.microchipId || 'fara CIP'})`}
              loading={searchingFather}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tata (optional)"
                  placeholder="Cauta dupa nume sau microchip"
                  helperText="Selectati tatal (sex masculin) sau lasati necompletat"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchingFather ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              disabled={loading || Boolean(fatherId)}
            />
          )}

          {/* Data fatarii */}
          <TextField
            fullWidth
            required
            type="date"
            label="Data Fatarii"
            value={whelpingDate}
            onChange={(e) => setWhelpingDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
            disabled={loading}
          />

          {/* Numar pui */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Numar Pui *
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                onClick={() => setPupsDeclared(Math.max(1, pupsDeclared - 1))}
                disabled={loading || pupsDeclared <= 1}
              >
                <RemoveIcon />
              </IconButton>
              <TextField
                type="number"
                value={pupsDeclared}
                onChange={(e) => setPupsDeclared(Math.max(1, Math.min(20, Number(e.target.value))))}
                inputProps={{ min: 1, max: 20 }}
                sx={{ width: 100 }}
                disabled={loading}
              />
              <IconButton
                onClick={() => setPupsDeclared(Math.min(20, pupsDeclared + 1))}
                disabled={loading || pupsDeclared >= 20}
              >
                <AddIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                (1-20)
              </Typography>
            </Stack>
          </Box>

          {/* Notite */}
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Notite"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading}
          />

          <Divider />

          {/* Sloturi pui */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Pui ({pups.length})
            </Typography>
            
            <Stack spacing={2}>
              {pups.map((pup, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Slot #{index + 1}
                  </Typography>
                  
                  <Stack spacing={2}>
                    {/* Microchip */}
                    <TextField
                      fullWidth
                      required
                      label="Microchip"
                      value={pup.microchip}
                      onChange={(e) => handlePupChange(index, 'microchip', e.target.value)}
                      onBlur={(e) => validateMicrochip(e.target.value, index)}
                      disabled={loading}
                      helperText={pup.mode === 'link' ? '✓ Legat la animal existent' : 'Va fi creat animal nou'}
                    />

                    {/* Sex */}
                    <FormControl fullWidth>
                      <InputLabel>Sex *</InputLabel>
                      <Select
                        value={pup.sex}
                        onChange={(e) => handlePupChange(index, 'sex', e.target.value)}
                        label="Sex *"
                        disabled={loading || pup.mode === 'link'}
                      >
                        <MenuItem value="M">Mascul</MenuItem>
                        <MenuItem value="F">Femela</MenuItem>
                        <MenuItem value="U">Necunoscut</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Nume */}
                    <TextField
                      fullWidth
                      label="Nume Pui"
                      value={pup.name}
                      onChange={(e) => handlePupChange(index, 'name', e.target.value)}
                      disabled={loading || pup.mode === 'link'}
                    />

                    {/* Campuri pentru mode=create */}
                    {pup.mode === 'create' && (
                      <>
                        <TextField
                          fullWidth
                          label="Specie"
                          value={pup.species || selectedMother?.species || ''}
                          onChange={(e) => handlePupChange(index, 'species', e.target.value)}
                          disabled={loading}
                          helperText="Implicit mostenit de la mama"
                        />
                        
                        <TextField
                          fullWidth
                          label="Rasa"
                          value={pup.breed || selectedMother?.breed || ''}
                          onChange={(e) => handlePupChange(index, 'breed', e.target.value)}
                          disabled={loading}
                          helperText="Implicit mostenit de la mama"
                        />
                        
                        <TextField
                          fullWidth
                          type="date"
                          label="Data Nasterii"
                          value={pup.birthdate || whelpingDate}
                          onChange={(e) => handlePupChange(index, 'birthdate', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          disabled={loading}
                          helperText="Implicit data fatarii"
                        />
                      </>
                    )}

                    {/* Mode indicator */}
                    <Box>
                      <Chip
                        size="small"
                        icon={pup.mode === 'link' ? <LinkIcon /> : <PersonAddIcon />}
                        label={pup.mode === 'link' ? 'Legat la animal existent' : 'Se creeaza animal nou'}
                        color={pup.mode === 'link' ? 'success' : 'primary'}
                      />
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Renunta
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !selectedMother || !selectedMother?.id || !whelpingDate || pups.some(p => !p.microchip)}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Se salveaza...' : 'Salveaza Fatarea'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Custom hook pentru debounce
function useDebounce(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default LitterDialog;
